import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Typography, Space, InputNumber, Input,
  message, Tag, Spin, Tooltip, Form, Modal,
} from 'antd';
import {
  SettingOutlined, EditOutlined, SaveOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Title, Text } = Typography;

const CONFIG_META: Record<string, { label: string; desc: string; type: 'number' | 'text'; unit?: string }> = {
  trial_duration_days:        { label: 'Trial Duration', type: 'number', unit: 'days', desc: 'How many days the free trial lasts for new users' },
  free_practice_per_day:      { label: 'Free Practice / Day', type: 'number', unit: 'sessions', desc: 'Max practice sessions a free user can do per day' },
  free_tests_per_day:         { label: 'Free Tests / Day', type: 'number', unit: 'tests', desc: 'Max mock tests a free user can attempt per day' },
  free_questions_per_session: { label: 'Free Questions / Session', type: 'number', unit: 'questions', desc: 'Max questions shown per practice session for free users' },
};

const AppConfigPage: React.FC = () => {
  const [configs, setConfigs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editing, setEditing]     = useState<any>(null);
  const [saving, setSaving]       = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAppConfig();
      setConfigs(res.data ?? []);
    } catch {
      message.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (row: any) => {
    setEditing(row);
    form.setFieldsValue({ value: row.value, description: row.description });
    setEditModal(true);
  };

  const handleSave = async () => {
    const vals = form.getFieldsValue();
    setSaving(true);
    try {
      await adminAPI.updateAppConfig(editing.key, vals.value, vals.description);
      message.success('Config updated');
      setEditModal(false);
      load();
    } catch {
      message.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getMeta = (key: string) => CONFIG_META[key] ?? { label: key, type: 'text', desc: '' };

  const columns = [
    {
      title: 'Setting',
      dataIndex: 'key',
      render: (key: string) => {
        const m = getMeta(key);
        return (
          <Space direction="vertical" size={0}>
            <Space>
              <Text strong style={{ fontSize: 13 }}>{m.label}</Text>
              {m.desc && (
                <Tooltip title={m.desc}>
                  <InfoCircleOutlined style={{ color: '#9CA3AF', fontSize: 13 }} />
                </Tooltip>
              )}
            </Space>
            <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{key}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Current Value',
      dataIndex: 'value',
      render: (val: string, row: any) => {
        const m = getMeta(row.key);
        return (
          <Space>
            <Tag color="blue" style={{ fontSize: 14, padding: '2px 12px', fontWeight: 700 }}>
              {val}
            </Tag>
            {m.unit && <Text type="secondary" style={{ fontSize: 12 }}>{m.unit}</Text>}
          </Space>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (desc: string, row: any) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {desc || getMeta(row.key).desc || '—'}
        </Text>
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated_at',
      render: (d: string) => d
        ? <Text style={{ fontSize: 12 }}>{new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: '',
      key: 'action',
      render: (_: any, row: any) => (
        <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(row)}>Edit</Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 4px' }}>
      <Space style={{ marginBottom: 20 }} align="center">
        <SettingOutlined style={{ fontSize: 22, color: '#6366F1' }} />
        <Title level={3} style={{ margin: 0 }}>App Configuration</Title>
      </Space>

      <Card
        bordered={false}
        style={{ borderRadius: 14 }}
        extra={<Button icon={<SaveOutlined />} onClick={load} size="small">Refresh</Button>}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          These settings control free-tier limits and trial behaviour for NammaPrep users.
          Changes take effect immediately for all new sessions.
        </Text>
        <Spin spinning={loading}>
          <Table
            dataSource={configs}
            columns={columns}
            rowKey="key"
            pagination={false}
            size="middle"
          />
        </Spin>
      </Card>

      <Modal
        open={editModal}
        title={`Edit: ${editing ? getMeta(editing.key).label : ''}`}
        onCancel={() => setEditModal(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        okButtonProps={{ icon: <SaveOutlined /> }}
      >
        {editing && (
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item label="Value" name="value" rules={[{ required: true }]}>
              {getMeta(editing.key).type === 'number'
                ? <InputNumber min={0} max={9999} style={{ width: '100%' }} addonAfter={getMeta(editing.key).unit} />
                : <Input />
              }
            </Form.Item>
            <Form.Item label="Description (optional)" name="description">
              <Input placeholder="Short description of what this controls" />
            </Form.Item>
            {getMeta(editing.key).desc && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                <InfoCircleOutlined /> {getMeta(editing.key).desc}
              </Text>
            )}
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default AppConfigPage;
